/**
 * NI-25 — Northline's portfolio review, on the Dashboard.
 *
 * Reads GET /api/intelligence/portfolio-review: the state of the last
 * request and the last published review — the off-plan properties in the
 * order to look at them, the themes that repeat across buildings, and
 * Northline's own numbers beside both (the model states none). The ONE
 * write is asking for a review now (POST …/portfolio-review/run; "Review
 * Again" forces a fresh call on the same numbers), offered only to a person
 * who may operate intelligence. The decision is made in the Review Queue,
 * the ONE place Northline tells a person what needs attention; this card
 * links there and never decides. Renders nothing for a person without
 * intelligence:view.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, Search, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { ITEM_STATUS_LABEL, SEV_CHIP } from '../properties/varianceInvestigation';
import { STATE_LABEL, patternLine, propertyLine, tooFewLine, totalsLine } from './portfolioReview';

import { apiDetail } from '../../lib/apiError';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortfolioReviewCard() {
  const { token, hasPermission } = useAuth();
  const canView = hasPermission('intelligence:view');
  const canOperate = hasPermission('intelligence:operate');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [asking, setAsking] = useState(false);
  const pollTimer = useRef(null);
  const pollsLeft = useRef(0);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchState = useCallback(async () => {
    if (!canView) return null;
    try {
      const res = await axios.get(`${API_URL}/api/intelligence/portfolio-review`, { headers });
      setError('');
      return res.data;
    } catch (e) {
      if (e?.response?.status !== 403) setError(apiDetail(e) || e.message || 'Could not load the portfolio review');
      return null;
    }
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPolling = useCallback(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = null;
    pollsLeft.current = 0;
  }, []);

  // Poll every 3 seconds while a request is queued or running, for at most
  // three minutes; the reasoning tier reads the whole portfolio.
  const pollWhileInFlight = useCallback((state) => {
    const inFlight = state && (state.status === 'queued' || state.status === 'running');
    if (!inFlight || pollsLeft.current <= 0) { stopPolling(); return; }
    pollsLeft.current -= 1;
    pollTimer.current = setTimeout(async () => {
      const next = await fetchState();
      if (next) setData(next);
      pollWhileInFlight(next);
    }, 3000);
  }, [fetchState, stopPolling]);

  const startPolling = useCallback((state) => {
    stopPolling();
    pollsLeft.current = 60;
    pollWhileInFlight(state);
  }, [pollWhileInFlight, stopPolling]);

  useEffect(() => {
    const ctl = { cancelled: false };
    (async () => {
      const state = await fetchState();
      if (ctl.cancelled || !state) return;
      setData(state);
      startPolling(state);
    })();
    return () => { ctl.cancelled = true; };
  }, [fetchState]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  const ask = async (force = false) => {
    if (asking) return;
    setAsking(true);
    try {
      const res = await axios.post(`${API_URL}/api/intelligence/portfolio-review/run`, { force }, { headers });
      setData(res.data);
      toast.success(res.data.request?.created
        ? 'Portfolio review requested. It appears here and in the Review Queue within a few minutes.'
        : 'Northline has already reviewed these numbers.');
      startPolling(res.data);
    } catch (e) {
      toast.error(apiDetail(e) || 'Could not request a portfolio review');
    } finally {
      setAsking(false);
    }
  };

  if (!canView || !data) return null;

  const status = data.status || 'none';
  const review = data.review;
  const item = data.item;
  const inFlight = asking || status === 'queued' || status === 'running';
  const switchedOff = data.intelligence_enabled === false;
  const tooFew = tooFewLine(data);
  const problem = (status === 'refused' || status === 'failed' || status === 'skipped') ? data.error : null;
  const hasRun = status !== 'none';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6"
         data-testid="portfolio-review-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Northline's portfolio review</h3>
            {data.generated_at && (
              <span className="text-[10px] text-slate-400" data-testid="portfolio-review-generated-at">
                · {new Date(data.generated_at).toLocaleString()}{data.run?.model ? ` · ${data.run.model}` : ''}
              </span>
            )}
          </div>
          {review?.totals && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-testid="portfolio-review-totals">
              {totalsLine(review.totals)}{review.as_of ? ` · as of ${review.as_of}` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {item && (
            <Link to={item.link} className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-0.5"
                  data-testid="portfolio-review-open">
              Open in Review Queue <ExternalLink className="w-3 h-3" />
            </Link>
          )}
          {canOperate && (
            <Button variant="outline" size="sm" onClick={() => ask(hasRun)} disabled={inFlight || switchedOff || Boolean(tooFew)}
                    className="h-7 text-xs" data-testid="portfolio-review-btn">
              {inFlight
                ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                : hasRun ? <RefreshCw className="w-3 h-3 mr-1" /> : <Search className="w-3 h-3 mr-1" />}
              {inFlight ? 'Reviewing…' : hasRun ? 'Review Again' : 'Review the Portfolio'}
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-600" data-testid="portfolio-review-error">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}

      {switchedOff && (
        <p className="text-xs text-slate-500 italic" data-testid="portfolio-review-switched-off">
          Northline Intelligence is switched off for this company.
        </p>
      )}

      {!switchedOff && tooFew && (
        <p className="text-sm text-slate-500 italic py-2" data-testid="portfolio-review-too-few">{tooFew}</p>
      )}

      {!switchedOff && inFlight && (
        <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="portfolio-review-in-flight">
          <Loader2 className="w-3 h-3 animate-spin" /> {STATE_LABEL[status] || STATE_LABEL.running}
        </p>
      )}

      {problem && (
        <p className="text-xs text-slate-500 italic" data-testid="portfolio-review-problem">{STATE_LABEL[status]}: {problem}</p>
      )}

      {status === 'on_plan' && !inFlight && (
        <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5 py-2" data-testid="portfolio-review-on-plan">
          <CheckCircle2 className="w-4 h-4" /> Every measured property is on plan — no variance clears the materiality floor.
        </p>
      )}

      {status === 'none' && !inFlight && !switchedOff && !tooFew && (
        <p className="text-sm text-slate-500 italic py-2" data-testid="portfolio-review-not-yet">
          Not reviewed yet. Northline measures every property against its budget and underwriting, then says where to look first and what repeats across buildings.
        </p>
      )}

      {item && (
        <p className="text-xs text-slate-500 mb-2" data-testid="portfolio-review-item">
          <span className={`px-2 py-0.5 rounded-full mr-2 ${SEV_CHIP[item.severity] || SEV_CHIP.low}`}>{item.severity}</span>
          {ITEM_STATUS_LABEL[item.status] || item.status}
        </p>
      )}

      {review && status !== 'on_plan' && (
        <div className="space-y-3" data-testid="portfolio-review-body">
          <p className="text-sm text-slate-800 dark:text-slate-200">{review.headline}</p>

          {(review.notes || []).map((n, i) => (
            <p key={i} className="text-xs text-amber-700 dark:text-amber-400" data-testid={`portfolio-review-note-${i}`}>{n}</p>
          ))}

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Where to look first</h4>
            <div className="space-y-2">
              {(review.priorities || []).map((p) => (
                <div key={p.property.property_id} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2"
                     data-testid={`portfolio-review-priority-${p.rank}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-[10px] text-slate-400 font-mono">{p.rank}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEV_CHIP[p.severity] || SEV_CHIP.low}`}>{p.severity}</span>
                    <Link to={`/properties/${p.property.property_id}`} className="text-xs font-semibold text-indigo-600 hover:underline">
                      {p.property.name || `Property ${p.property.property_id}`}
                    </Link>
                    {p.ranked_by === 'northline' && (
                      <span className="text-[10px] text-slate-400">ordered by exposure</span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 dark:text-slate-300">{propertyLine(p.property)}</p>
                  {p.why && <p className="text-sm text-slate-800 dark:text-slate-200 mt-1">{p.why}</p>}
                  {p.next_step && (
                    <p className="text-[11px] text-slate-700 dark:text-slate-200 mt-1"><span className="font-medium">Next:</span> {p.next_step}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {(review.themes || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">What repeats across properties</h4>
              <div className="space-y-2">
                {review.themes.map((t, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid={`portfolio-review-theme-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEV_CHIP[t.severity] || SEV_CHIP.low}`}>{t.severity}</span>
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">{t.title}</span>
                    </div>
                    <p className="text-sm text-slate-800 dark:text-slate-200">{t.detail}</p>
                    {(t.patterns || []).length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {t.patterns.map((pt) => (
                          <li key={`${pt.ref}-${pt.direction}`} className="text-[11px] text-slate-600 dark:text-slate-300">{patternLine(pt)}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">
                      {(t.properties || []).map((x) => x.name || `Property ${x.property_id}`).join(' · ')}
                    </p>
                    {t.next_step && (
                      <p className="text-[11px] text-slate-700 dark:text-slate-200 mt-1"><span className="font-medium">Next:</span> {t.next_step}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-200 dark:border-slate-700">
            Northline measured every property and computed every number here; the model ordered the properties and named what repeats. Decide on it in the Review Queue.
          </p>
        </div>
      )}
    </div>
  );
}
